import React, { useContext, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getVisitReport, getVisitProducts } from "../../API";

function formatDate(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-CA");
}

function formatTime(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleTimeString("en-CA", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function isIncompletePair(io) {
  return !io.startDate || !io.endDate || io.endDate <= io.startDate;
}

// Flags a single in/out session as a likely missed checkout: it crossed midnight
// and ran long enough (4h+) that it's implausible as real time spent in-store —
// consistent with the rep leaving without ending the visit.
function isLikelyStaleCheckout(io) {
  if (isIncompletePair(io)) return false;
  const STALE_DURATION_MS = 4 * 60 * 60 * 1000;
  if (io.endDate - io.startDate < STALE_DURATION_MS) return false;
  const startDay = new Date(io.startDate).toLocaleDateString("en-CA");
  const endDay = new Date(io.endDate).toLocaleDateString("en-CA");
  return startDay !== endDay;
}

function formatInOutDuration(inOuts) {
  if (!inOuts || inOuts.length === 0) return "";
  const seen = new Set();
  const totalMs = inOuts.reduce((sum, io) => {
    if (isIncompletePair(io)) return sum;
    const key = `${io.startDate}_${io.endDate}`;
    if (seen.has(key)) return sum;
    seen.add(key);
    return sum + (io.endDate - io.startDate);
  }, 0);
  if (totalMs <= 0) return "0:00";
  const totalMin = Math.floor(totalMs / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function tagBadge(tag) {
  if (tag === "YES") return <span className="badge bg-success">Yes</span>;
  if (tag === "NO") return <span className="badge bg-danger">No</span>;
  return <span className="text-muted">—</span>;
}

export default function VisitReport() {
  const [{ fromDate, toDate, representatives }] = useContext(ReportParamsContext);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedVisits, setExpandedVisits] = useState(new Set());
  const [expandedMfrs, setExpandedMfrs] = useState({});
  const [productsCache, setProductsCache] = useState({});

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setExpandedVisits(new Set());
    setExpandedMfrs({});
    setProductsCache({});
    try {
      const data = await getVisitReport({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        representativeIds: representatives.map((r) => r.id),
      });
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (visitId) => {
    setProductsCache((prev) => ({ ...prev, [visitId]: { loading: true } }));
    try {
      const manufacturers = await getVisitProducts(visitId);
      setProductsCache((prev) => ({ ...prev, [visitId]: { loading: false, manufacturers } }));
    } catch (e) {
      setProductsCache((prev) => ({ ...prev, [visitId]: { loading: false, manufacturers: [], error: e.message } }));
    }
  };

  const toggleVisit = (visitId) => {
    setExpandedVisits((prev) => {
      const next = new Set(prev);
      if (next.has(visitId)) {
        next.delete(visitId);
      } else {
        next.add(visitId);
        if (!productsCache[visitId]) fetchProducts(visitId);
      }
      return next;
    });
  };

  const toggleMfr = (visitId, mfrId) => {
    setExpandedMfrs((prev) => {
      const visitSet = new Set(prev[visitId] ?? []);
      visitSet.has(mfrId) ? visitSet.delete(mfrId) : visitSet.add(mfrId);
      return { ...prev, [visitId]: visitSet };
    });
  };

  const visits = report?.visits ?? [];

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
              <div className="col-md-auto text-muted small">
                Use the date range and representative filters in the sidebar to narrow results.
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {report && (
          <>
            <div className="d-flex align-items-center gap-3 text-muted small mb-2">
              <span>{visits.length} visit{visits.length !== 1 ? "s" : ""}</span>
              <span><span className="me-1">⏰</span>likely missed checkout (session ran 4h+ and crossed midnight)</span>
              <span><span className="me-1">⚠</span>incomplete in/out pair</span>
            </div>
            <div className="table-responsive">
              <table
                className="table table-hover table-bordered mb-0"
                style={{ fontSize: "0.82rem" }}
              >
                <thead className="table-dark">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Date</th>
                    <th style={{ whiteSpace: "nowrap" }}>Representative</th>
                    <th>Class</th>
                    <th style={{ minWidth: 130 }}>Banner</th>
                    <th>Store No</th>
                    <th style={{ minWidth: 160 }}>Address</th>
                    <th>City</th>
                    <th style={{ whiteSpace: "nowrap" }}>In / Out</th>
                    <th style={{ whiteSpace: "nowrap" }}>Duration</th>
                    <th>Facing</th>
                    <th style={{ whiteSpace: "nowrap" }}>Distribution %</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center text-muted py-4">
                        No visits found for the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    visits.map((visit) => {
                      const isOpen = expandedVisits.has(visit.visitId);
                      return (
                        <React.Fragment key={visit.visitId}>
                          <tr
                            onClick={() => toggleVisit(visit.visitId)}
                            style={{ cursor: "pointer" }}
                            className={isOpen ? "table-active" : ""}
                          >
                            <td style={{ whiteSpace: "nowrap" }}>
                              <span className="me-1" style={{ fontSize: "0.7rem", color: "#666" }}>
                                {isOpen ? "▼" : "▶"}
                              </span>
                              {formatDate(visit.visitStart)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>{visit.representativeName}</td>
                            <td>{visit.storeClass}</td>
                            <td>{visit.bannerName}</td>
                            <td>{visit.storeNumber}</td>
                            <td>{visit.storeAddress}</td>
                            <td>{visit.storeCity}</td>
                            <td>
                              {visit.inOuts.map((io, i) => {
                                const incomplete = isIncompletePair(io);
                                const stale = !incomplete && isLikelyStaleCheckout(io);
                                return (
                                  <div
                                    key={i}
                                    style={{ whiteSpace: "nowrap", color: incomplete ? "#dc3545" : stale ? "#fd7e14" : undefined }}
                                    title={
                                      incomplete
                                        ? "Incomplete in/out pair — missing a check-in or check-out time"
                                        : stale
                                        ? "Likely missed checkout — this session ran 4+ hours and crossed midnight, suggesting the rep left without ending the visit"
                                        : undefined
                                    }
                                  >
                                    {incomplete && <span className="me-1">⚠</span>}
                                    {stale && <span className="me-1">⏰</span>}
                                    {formatTime(io.startDate)}
                                    <span className={incomplete ? undefined : "text-muted"} style={{ margin: "0 0.25rem" }}>→</span>
                                    {formatTime(io.endDate)}
                                  </div>
                                );
                              })}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatInOutDuration(visit.inOuts)}
                              {visit.inOuts.some(isIncompletePair) && (
                                <span
                                  className="text-danger ms-1"
                                  title="Duration excludes incomplete in/out pairs — actual time on site may be longer"
                                >
                                  ⚠
                                </span>
                              )}
                              {visit.inOuts.some((io) => !isIncompletePair(io) && isLikelyStaleCheckout(io)) && (
                                <span
                                  className="ms-1"
                                  style={{ color: "#fd7e14" }}
                                  title="Duration includes a likely missed-checkout session — this inflates the time shown"
                                >
                                  ⏰
                                </span>
                              )}
                            </td>
                            <td>{visit.totalFacing}</td>
                            <td>{visit.distributionPct.toFixed(2)}%</td>
                          </tr>

                          {isOpen && (
                            <tr>
                              <td
                                colSpan={12}
                                style={{ backgroundColor: "#f8f9fa", padding: "0.75rem 1.5rem" }}
                              >
                                {(() => {
                                  const cache = productsCache[visit.visitId];
                                  if (!cache || cache.loading) {
                                    return (
                                      <div className="d-flex align-items-center gap-2 text-muted small">
                                        <div className="spinner-border spinner-border-sm" role="status" />
                                        Loading products…
                                      </div>
                                    );
                                  }
                                  const manufacturers = cache.manufacturers ?? [];
                                  if (manufacturers.length === 0) {
                                    return <span className="text-muted small">No product data captured for this visit.</span>;
                                  }
                                  return (
                                  <div className="d-flex flex-column gap-2">
                                    {manufacturers.map((mfr) => {
                                      const mfrOpen = expandedMfrs[visit.visitId]?.has(mfr.id) ?? false;
                                      return (
                                        <div
                                          key={mfr.id}
                                          className="border rounded bg-white overflow-hidden"
                                        >
                                          <div
                                            className="d-flex align-items-center px-3 py-2"
                                            style={{
                                              cursor: "pointer",
                                              backgroundColor: mfrOpen ? "#e9ecef" : "#f8f9fa",
                                              fontWeight: 600,
                                              fontSize: "0.83rem",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleMfr(visit.visitId, mfr.id);
                                            }}
                                          >
                                            <span className="me-2" style={{ fontSize: "0.7rem", color: "#666" }}>
                                              {mfrOpen ? "▼" : "▶"}
                                            </span>
                                            {mfr.name}
                                            <span className="ms-2 badge bg-secondary fw-normal">
                                              {mfr.products.length} product{mfr.products.length !== 1 ? "s" : ""}
                                            </span>
                                          </div>

                                          {mfrOpen && (
                                            <table
                                              className="table table-sm table-bordered mb-0"
                                              style={{ fontSize: "0.8rem" }}
                                            >
                                              <thead className="table-light">
                                                <tr>
                                                  <th>Product</th>
                                                  <th>UPC</th>
                                                  <th>Facing</th>
                                                  <th>Price</th>
                                                  <th>Price Status</th>
                                                  <th>Tag</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {mfr.products.map((p, pi) => (
                                                  <tr key={pi}>
                                                    <td>{p.description}</td>
                                                    <td style={{ whiteSpace: "nowrap" }}>{p.upcCode}</td>
                                                    <td>{p.facing}</td>
                                                    <td style={{ whiteSpace: "nowrap" }}>{p.price}</td>
                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                      {p.priceStatus}
                                                      {p.priceStatus === "Promo" && p.promoName && (
                                                        <span className="ms-1 text-muted">({p.promoName})</span>
                                                      )}
                                                    </td>
                                                    <td>{tagBadge(p.tag)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
